// «Вход через сервисы» в «Личных данных»: привязка и отвязка Яндекс ID и
// VK ID (ТЗ, п. 1.2). Кадр профиля этого блока не рисует - он стоит под
// формой, рядом со ссылками «Адреса доставки» и «Устройства и сеансы», в том
// же языке: строки на #1F1F1F 3 % r20, вторичная ссылка-действие справа.
// Без подключённых провайдеров блока нет.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { api, type OAuthIdentity, type OAuthProviderInfo } from "@/api";
import { OAuthProviderIcon, goToOAuth, useOAuthProviders } from "./oauth-buttons";
const ERRORS = ["denied", "expired", "provider", "blocked", "taken", "not_configured"] as const;
export function OAuthLinks() {
  const {
    t
  } = useTranslation();
  const providers = useOAuthProviders();
  const [identities, setIdentities] = useState<OAuthIdentity[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (!providers.length) return;
    void api.oauth.identities().then(setIdentities).catch(() => setIdentities([]));
  }, [providers.length]);

  // Возврат от провайдера после привязки: ?oauth=linked или ?error=…
  useEffect(() => {
    const linked = params.get("oauth");
    const error = params.get("error");
    if (!linked && !error || !providers.length) return;
    const title = providers.find(p => p.id === params.get("provider"))?.title ?? "";
    if (linked === "linked") toast.success(t("oauth.linkedToast", {
      provider: title
    }));else if (error) {
      const code = ERRORS.find(e => e === error) ?? "expired";
      toast.error(t(`oauth.error.${code}`, {
        provider: title
      }));
    }
    const next = new URLSearchParams(params);
    for (const k of ["oauth", "error", "provider"]) next.delete(k);
    setParams(next, {
      replace: true
    });
  }, [params, providers, setParams, t]);
  if (!providers.length) return null;
  const unlink = async (p_0: OAuthProviderInfo) => {
    setBusy(p_0.id);
    try {
      await api.oauth.unlink(p_0.id);
      setIdentities(list => list.filter(i => i.provider !== p_0.id));
      toast.success(t("oauth.section.unlinked", {
        provider: p_0.title
      }));
    } catch {
      toast.error(t("oauth.section.failed"));
    } finally {
      setBusy(null);
    }
  };
  const link = async (p_1: OAuthProviderInfo) => {
    setBusy(p_1.id);
    await goToOAuth(p_1, "link", "/profile/personal", t("oauth.startFailed", {
      provider: p_1.title
    }));
    setBusy(null);
  };
  return <section className="mt-8" data-testid="oauth-links">
      <h2 className="text-[16px] leading-[21px] font-semibold text-[#1F1F1F]">
        {t("oauth.section.title")}
      </h2>
      <p className="mt-1 text-[14px] leading-[18px] text-[#171A24]/64">{t("oauth.section.hint")}</p>
      <ul className="mt-3 flex flex-col gap-2">
        {providers.map(p_2 => {
        const linked_0 = identities.find(i_0 => i_0.provider === p_2.id);
        return <li key={p_2.id} className="flex min-h-12 items-center gap-3 rounded-[20px] bg-[#1F1F1F]/[0.03] px-4 py-2" data-testid={`oauth-link-${p_2.id}`}>
              <OAuthProviderIcon provider={p_2.id} />
              <div className="min-w-0 flex-1">
                <p className="text-[16px] leading-[21px] font-medium text-[#1F1F1F]">{p_2.title}</p>
                <p className="truncate text-[13px] leading-[17px] text-[#1F1F1F]/56">
                  {linked_0 ? [t("oauth.section.linked"), linked_0.email ?? linked_0.displayName].filter(Boolean).join(" · ") : t("oauth.section.notLinked")}
                </p>
              </div>
              <button type="button" disabled={busy !== null} onClick={() => void (linked_0 ? unlink(p_2) : link(p_2))} className="shrink-0 rounded-sm text-[14px] leading-[18px] text-[#1F1F1F] outline-none hover:underline focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40 disabled:opacity-60">
                {linked_0 ? t("oauth.section.unlink") : t("oauth.section.link")}
              </button>
            </li>;
      })}
      </ul>
    </section>;
}